import { useEffect, useMemo, useState } from 'react'
import { Alert, Button, Checkbox, Space, Table, Tooltip, Typography, message } from 'antd'
import { rolesApi, type RoleInfo } from '../services/rolesApi'
import { useRolesStore } from '../store/rolesStore'
import { useAuthStore } from '../store/authStore'
import { DEV_ADMIN } from '../constants/roles'

const { Text } = Typography

function extractError(error: unknown): string {
    const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
    if (typeof detail === 'string') return detail
    return 'Не удалось выполнить действие'
}

type Row =
    | { key: string; kind: 'group'; label: string }
    | { key: string; kind: 'status'; code: string; label: string }

// Какие статусы CRM Wiseway каждая роль может выставить сама: в карточке лида,
// в окне «Изменить статус», при закрытии и переоткрытии диалога. Автоматические
// смены - правила SLA, рассылку, автораспределение - таблица не ограничивает.
export default function StatusAccessSettings() {
    const catalog = useRolesStore((state) => state.catalog)
    const load = useRolesStore((state) => state.load)
    const { user } = useAuthStore()
    const [savingKey, setSavingKey] = useState<string | null>(null)

    useEffect(() => {
        load().catch((error) => message.error(extractError(error)))
    }, [load])

    const roles = catalog?.roles ?? []
    const isDev = user?.role === DEV_ADMIN
    const myStatuses = useMemo(() => new Set(user?.allowed_statuses ?? []), [user])
    const allCodes = useMemo(
        () => (catalog?.status_groups ?? []).flatMap((group) => group.reasons.map((item) => item.code)),
        [catalog],
    )

    // Те же ограничения, что на сервере: свою роль СуперАдминистратор не
    // правит, а выдать может только те статусы, что доступны ему самому.
    const isOwnRole = (role: RoleInfo) => !isDev && role.code === user?.role
    const grantable = (code: string) => isDev || myStatuses.has(code)

    // Статусы идут группами - так же, как в их воронке.
    const rows = useMemo<Row[]>(() => {
        const result: Row[] = []
        for (const group of catalog?.status_groups ?? []) {
            result.push({ key: `group:${group.group}`, kind: 'group', label: group.group })
            for (const item of group.reasons) {
                result.push({ key: item.code, kind: 'status', code: item.code, label: item.label })
            }
        }
        return result
    }, [catalog])

    const save = async (role: RoleInfo, next: string[], key: string) => {
        setSavingKey(key)
        try {
            await rolesApi.update(role.code, { allowed_statuses: next })
            await load()
        } catch (error) {
            message.error(extractError(error))
        } finally {
            setSavingKey(null)
        }
    }

    const toggle = (role: RoleInfo, code: string, checked: boolean) => {
        const next = checked
            ? [...role.allowed_statuses, code]
            : role.allowed_statuses.filter((item) => item !== code)
        save(role, next, `${role.code}:${code}`)
    }

    // «Все» добавляет только то, что вправе выдать сам нажимающий.
    const selectAll = (role: RoleInfo) => {
        const next = new Set(role.allowed_statuses)
        for (const code of allCodes) if (grantable(code)) next.add(code)
        save(role, [...next], `${role.code}:all`)
    }

    const columns = [
        {
            title: 'Статус',
            key: 'label',
            fixed: 'left' as const,
            width: 280,
            onCell: (row: Row) => (row.kind === 'group' ? { colSpan: roles.length + 1 } : {}),
            render: (_: unknown, row: Row) => row.kind === 'group'
                ? <Text strong type="secondary">{row.label}</Text>
                : <Text>{row.label}</Text>,
        },
        ...roles.map((role) => ({
            key: role.code,
            width: 125,
            align: 'center' as const,
            title: (
                <Space direction="vertical" size={2} style={{ alignItems: 'center' }}>
                    <Text strong>{role.label}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {role.locked ? 'все' : `${role.allowed_statuses.length} из ${allCodes.length}`}
                    </Text>
                    {!role.locked && !isOwnRole(role) && (
                        <Space size={0}>
                            <Button
                                type="link"
                                size="small"
                                disabled={savingKey !== null}
                                onClick={() => selectAll(role)}
                            >
                                все
                            </Button>
                            <Button
                                type="link"
                                size="small"
                                disabled={savingKey !== null}
                                onClick={() => save(role, [], `${role.code}:none`)}
                            >
                                снять
                            </Button>
                        </Space>
                    )}
                </Space>
            ),
            onCell: (row: Row) => (row.kind === 'group' ? { colSpan: 0 } : {}),
            render: (_: unknown, row: Row) => {
                if (row.kind === 'group') return null
                if (role.locked) {
                    return (
                        <Tooltip title="«Админ разработки» ставит любой статус">
                            <Checkbox checked disabled />
                        </Tooltip>
                    )
                }
                const checked = role.allowed_statuses.includes(row.code)
                let reason: string | null = null
                if (isOwnRole(role)) reason = 'Статусы своей роли меняет «Админ разработки»'
                else if (!checked && !grantable(row.code)) reason = 'Нельзя выдать статус, который недоступен вам'
                const box = (
                    <Checkbox
                        checked={checked}
                        disabled={savingKey !== null || reason !== null}
                        onChange={(event) => toggle(role, row.code, event.target.checked)}
                    />
                )
                return reason ? <Tooltip title={reason}>{box}</Tooltip> : box
            },
        })),
    ]

    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Alert
                type="info"
                showIcon
                message="Что роль может выставить сама"
                description={
                    'Галочка разрешает роли ставить статус в карточке лида, в окне «Изменить статус», '
                    + 'при закрытии и переоткрытии диалога - во всех местах сразу, обойти через другую '
                    + 'кнопку нельзя. Статус без галочки роль не видит в списках вовсе. Правила SLA, '
                    + 'рассылка и автораспределение этой таблицей не ограничиваются: это действия системы. '
                    + 'Сохраняется сразу, изменения видны в «Журнале действий».'
                }
            />
            <Table
                rowKey="key"
                size="small"
                columns={columns}
                dataSource={rows}
                pagination={false}
                loading={!catalog}
                scroll={{ x: 'max-content', y: 640 }}
            />
        </Space>
    )
}
